import { useState, useCallback, useMemo, useEffect } from 'react';
import type { ReportFilters } from '@/types/reports';
import { formatDateLocal } from '@/lib/date-utils';

interface AdminInfo {
  type?: string;
  level?: string;
  affiliate_id?: string;
}

interface UseReportFiltersOptions {
  initialFilters?: Partial<ReportFilters>;
  pageSize?: number;
  currentAdmin?: AdminInfo | null;
}

interface UseReportFiltersReturn {
  filters: ReportFilters;
  currentPage: number;
  pageSize: number;
  updateFilter: (key: keyof ReportFilters, value: string | undefined) => void;
  updateDateFilter: (key: 'from_date' | 'to_date', date: Date | undefined) => void;
  clearFilters: () => void;
  setCurrentPage: (page: number) => void;
  hasFilters: boolean;
  isAffiliate: boolean;
  affiliateFilterReady: boolean;
}

/**
 * Get admin info from sessionStorage synchronously for initial state
 */
function getAdminInfoFromSession(): AdminInfo | null {
  const adminInfo = sessionStorage.getItem('prosci_admin_info');
  if (adminInfo) {
    try {
      return JSON.parse(adminInfo);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Custom hook for managing report filter state and pagination
 * Automatically handles affiliate filtering for locked affiliate users
 */
export function useReportFilters(
  options: UseReportFiltersOptions = {}
): UseReportFiltersReturn {
  const { initialFilters = {}, pageSize = 20, currentAdmin } = options;

  // Get admin info synchronously for initial filter state (fallback to sessionStorage)
  const adminInfo = useMemo(() => currentAdmin || getAdminInfoFromSession(), [currentAdmin]);
  const isAffiliate = adminInfo?.type === 'prosci_admin' && adminInfo?.level === 'affiliate';
  const adminAffiliateId = adminInfo?.affiliate_id;

  // Initialize filters with affiliate_id pre-set for affiliate users
  const [filters, setFilters] = useState<ReportFilters>(() => {
    const baseFilters = { ...initialFilters };
    const sessionAdmin = getAdminInfoFromSession();
    const initIsAffiliate = sessionAdmin?.type === 'prosci_admin' && sessionAdmin?.level === 'affiliate';
    if (initIsAffiliate && sessionAdmin?.affiliate_id) {
      baseFilters.affiliate_id = sessionAdmin.affiliate_id;
    }
    return baseFilters;
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Update affiliate filter when currentAdmin becomes available (handles login redirect timing)
  // This is only needed for locked affiliate users whose filter must be set before queries run
  useEffect(() => {
    // Only set if filter is currently empty and admin data indicates we should have a filter
    if (!filters.affiliate_id && isAffiliate && adminAffiliateId) {
      setFilters(prev => ({ ...prev, affiliate_id: adminAffiliateId }));
    }
    // Note: filters.affiliate_id is intentionally NOT in deps - we only want this to run when admin data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAffiliate, adminAffiliateId]);

  const updateFilter = useCallback(
    (key: keyof ReportFilters, value: string | undefined) => {
      // Prevent affiliate users from changing their affiliate_id filter (it's locked)
      if (key === 'affiliate_id' && isAffiliate) {
        return;
      }
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    [isAffiliate]
  );

  const updateDateFilter = useCallback(
    (key: 'from_date' | 'to_date', date: Date | undefined) => {
      setFilters((prev) => ({
        ...prev,
        [key]: date ? formatDateLocal(date) : undefined,
      }));
      setCurrentPage(1);
    },
    []
  );

  const clearFilters = useCallback(() => {
    // For affiliate users, preserve their locked affiliate_id when clearing
    if (isAffiliate && adminAffiliateId) {
      setFilters({ affiliate_id: adminAffiliateId });
    } else {
      setFilters({});
    }
    setCurrentPage(1);
  }, [isAffiliate, adminAffiliateId]);

  // For hasFilters, don't count affiliate_id for affiliate users since it's locked
  const hasFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      // For affiliate users, don't count their locked affiliate_id as a filter
      if (key === 'affiliate_id' && isAffiliate) {
        return false;
      }
      return value !== undefined && value !== '';
    });
  }, [filters, isAffiliate]);

  // For affiliate users (locked), don't run queries until the filter is set
  // This prevents queries from firing before the useEffect has a chance to set the filter
  // affiliate_admin users can clear their filter and see all data, so they're not blocked
  const affiliateFilterReady = !isAffiliate || !!filters.affiliate_id;

  return {
    filters,
    currentPage,
    pageSize,
    updateFilter,
    updateDateFilter,
    clearFilters,
    setCurrentPage,
    hasFilters,
    isAffiliate,
    affiliateFilterReady,
  };
}
